// src/api/dashboardMutations.ts
//
// One hook for every dashboard mutation (ADR-0228). The page has to know the
// row's review state after ANY write — revision (the If-Match a later Submit
// sends), review status (an approved draft is a draft again after one edit)
// and permissions — and it used to refresh that at each call site by hand,
// which left five of them bare. So the request layer notifies here after
// every successful mutation, and the page subscribes once.
//
// Kept apart from `dashboards.ts` on purpose: the page harnesses mock that
// module wholesale, and a test then fires `notifyDashboardMutated` from this
// real one.
import {
  apiJson as clientJson,
  apiNoContent as clientNoContent,
  type ApiJsonOptions,
} from "./client";

type Listener = (dashboardId: string) => void;

/** The id a listener receives when the mutation's path names an item, not a
 *  dashboard (`/dashboards/items/<item>/…`): "whichever dashboard is open". */
export const ANY_DASHBOARD = "*";

const listeners = new Set<Listener>();

export function subscribeDashboardMutations(listener: Listener): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function notifyDashboardMutated(dashboardId: string): void {
  for (const listener of Array.from(listeners)) {
    try {
      listener(dashboardId);
    } catch (err) {
      console.error("A dashboard-mutation listener failed:", err);
    }
  }
}

/**
 * Which dashboard a request mutates, from its method and path — or null when
 * it mutates none: a GET, a path outside `/dashboards/…`, or `POST /dashboards`
 * itself (a create has no id yet). A path whose first segment is not a
 * dashboard id (`/dashboards/items/…`) answers `ANY_DASHBOARD`.
 */
export function dashboardIdOfMutation(method: string | undefined, url: string): string | null {
  const verb = (method ?? "GET").toUpperCase();
  if (verb === "GET" || verb === "HEAD") return null;
  const match = /^\/dashboards\/([^/?#]+)(?:[/?#]|$)/.exec(url);
  if (!match) return null;
  const segment = decodeURIComponent(match[1]);
  return segment.startsWith("dash_") ? segment : ANY_DASHBOARD;
}

// The request wrappers of every dashboard module (`dashboards.ts`,
// `dashboardVersions.ts`). After a successful non-GET under
// `/dashboards/<id>/…` they notify the hook above, so the page can refresh the
// row's review state without a call at each of the forty-odd sites, and
// without one to forget. They live here, not in `dashboards.ts`, because the
// page harnesses mock that module wholesale and a sibling importing them from
// it would get the mock.
export async function apiJson<T>(url: string, init?: ApiJsonOptions): Promise<T> {
  const result = await clientJson<T>(url, init);
  const mutated = dashboardIdOfMutation(init?.method, url);
  if (mutated) notifyDashboardMutated(mutated);
  return result;
}

export async function apiNoContent(url: string, init?: RequestInit): Promise<void> {
  await clientNoContent(url, init);
  const mutated = dashboardIdOfMutation(init?.method, url);
  if (mutated) notifyDashboardMutated(mutated);
}
